import { useState, useEffect } from 'react';
import { SERVICE_TYPE_CONFIG, type PaymentServiceType } from '../types/payment.types';
import api from '../services/api';

export interface ServiceTypeConfigItem {
  label: string;
  referenceLabel: string;
  referenceHint: string;
  showEntityField: boolean;
  keyboardType?: string;
}

type ServiceTypeConfigMap = Record<string, ServiceTypeConfigItem>;

// Cache em memória (singleton entre hooks)
let cachedConfig: ServiceTypeConfigMap | null = null;
let fetchPromise: Promise<ServiceTypeConfigMap> | null = null;

const fetchConfig = async (): Promise<ServiceTypeConfigMap> => {
  try {
    const { data } = await api.get('/public/service-type-config');
    if (data.success && data.data && Object.keys(data.data).length > 0) {
      cachedConfig = data.data;
      return data.data;
    }
  } catch (e) {
    console.warn('Failed to load service type config from API, using fallback');
  }
  // Fallback para hardcoded
  return Object.fromEntries(
    Object.entries(SERVICE_TYPE_CONFIG).map(([k, v]) => [k, { ...v, keyboardType: 'text' }])
  );
};

/**
 * Hook que carrega a configuração de tipos de serviço da API.
 * Usa cache em memória e fallback hardcoded se a API falhar.
 */
export function useServiceTypeConfig() {
  const [config, setConfig] = useState<ServiceTypeConfigMap>(
    cachedConfig || Object.fromEntries(
      Object.entries(SERVICE_TYPE_CONFIG).map(([k, v]) => [k, { ...v, keyboardType: 'text' }])
    )
  );
  const [loading, setLoading] = useState(!cachedConfig);

  useEffect(() => {
    if (cachedConfig) return;
    if (!fetchPromise) fetchPromise = fetchConfig();
    fetchPromise.then(c => {
      setConfig(c);
      setLoading(false);
      fetchPromise = null;
    });
  }, []);

  const getConfig = (type: PaymentServiceType | string): ServiceTypeConfigItem => {
    return config[type] || config['WBS'] || {
      label: 'Serviço',
      referenceLabel: 'Referência',
      referenceHint: 'Insira a referência',
      showEntityField: false,
    };
  };

  return { config, loading, getConfig };
}
